import {
  CategorizationCreateRequirements,
  CategorizationLevel,
  CategorizationNode,
  CategorizationNodeLocation,
  CategorizationValidationError,
  EditableCategorizationNode,
} from '@totem/components/ticketing/categorizationManagement/types';

export const MAX_CATEGORIZATION_LEVEL: CategorizationLevel = 3;

export const ROLE_OPTIONS = [
  { value: -2, label: 'Tenant' },
  { value: -1, label: 'Vendor' },
  { value: 0, label: 'Collaborator' },
  { value: 1, label: 'Team Member' },
  { value: 2, label: 'Admin' },
  { value: 3, label: 'Systems Administrator' },
];

export const levelLabel = (level: CategorizationLevel): string => {
  switch (level) {
    case 1:
      return 'Ticket Type';
    case 2:
      return 'Category';
    default:
      return 'Sub-Category';
  }
};

export const childLevelLabel = (level: CategorizationLevel): string =>
  level < MAX_CATEGORIZATION_LEVEL
    ? levelLabel((level + 1) as CategorizationLevel)
    : '';

let keySequence = 0;
const nextKey = () => {
  keySequence += 1;
  return `node-${keySequence}`;
};

export const emptyRequirements = (): CategorizationCreateRequirements => ({
  internalOnly: false,
  roles: [],
  securityGroups: [],
  organizations: [],
});

export const hasRequirements = (
  requirements: CategorizationCreateRequirements,
): boolean =>
  requirements.internalOnly ||
  requirements.roles.length > 0 ||
  requirements.securityGroups.length > 0 ||
  requirements.organizations.length > 0;

export const toEditableNodes = (
  nodes: CategorizationNode[] | null | undefined,
): EditableCategorizationNode[] =>
  (nodes ?? []).map((node) => ({
    key: nextKey(),
    name: node.name,
    originalName: node.name,
    createRequirements: {
      internalOnly: node.createRequirements?.internalOnly ?? false,
      roles: node.createRequirements?.roles ?? [],
      securityGroups: node.createRequirements?.securityGroups ?? [],
      organizations: node.createRequirements?.organizations ?? [],
    },
    relatedServices: node.relatedServices ?? [],
    children: toEditableNodes(node.children),
  }));

export const toApiNodes = (
  nodes: EditableCategorizationNode[],
  level: CategorizationLevel = 1,
): CategorizationNode[] =>
  nodes.map((node) => {
    const apiNode: CategorizationNode = {
      name: node.name.trim(),
      createRequirements: hasRequirements(node.createRequirements)
        ? node.createRequirements
        : null,
    };
    if (node.relatedServices.length > 0) {
      apiNode.relatedServices = node.relatedServices;
    }
    if (level < MAX_CATEGORIZATION_LEVEL) {
      apiNode.children = toApiNodes(
        node.children,
        (level + 1) as CategorizationLevel,
      );
    }
    return apiNode;
  });

export const newNode = (name: string): EditableCategorizationNode => ({
  key: nextKey(),
  name,
  originalName: null,
  createRequirements: emptyRequirements(),
  relatedServices: [],
  children: [],
});

export const findNode = (
  nodes: EditableCategorizationNode[],
  key: string,
  level: CategorizationLevel = 1,
  ancestors: EditableCategorizationNode[] = [],
): CategorizationNodeLocation | null => {
  for (let idx = 0; idx < nodes.length; idx++) {
    const node = nodes[idx];
    if (node.key === key) {
      return { node, level, ancestors, siblings: nodes, index: idx };
    }
    if (level < MAX_CATEGORIZATION_LEVEL) {
      const found = findNode(
        node.children,
        key,
        (level + 1) as CategorizationLevel,
        [...ancestors, node],
      );
      if (found !== null) {
        return found;
      }
    }
  }
  return null;
};

export const findKeyByPath = (
  nodes: EditableCategorizationNode[],
  path: string[],
): string | null => {
  let current = nodes;
  let key: string | null = null;
  for (const name of path) {
    const match = current.find((node) => node.name === name);
    if (!match) {
      return null;
    }
    key = match.key;

    current = match.children;
  }
  return key;
};

// Applies updater to the node with the given key, returning a new tree.
export const updateNode = (
  nodes: EditableCategorizationNode[],
  key: string,
  updater: (node: EditableCategorizationNode) => EditableCategorizationNode,
): EditableCategorizationNode[] =>
  nodes.map((node) => {
    if (node.key === key) {
      return updater(node);
    }
    return node.children.length > 0
      ? { ...node, children: updateNode(node.children, key, updater) }
      : node;
  });

export const removeNode = (
  nodes: EditableCategorizationNode[],
  key: string,
): EditableCategorizationNode[] =>
  nodes
    .filter((node) => node.key !== key)
    .map((node) =>
      node.children.length > 0
        ? { ...node, children: removeNode(node.children, key) }
        : node,
    );

export const moveNode = (
  nodes: EditableCategorizationNode[],
  key: string,
  offset: number,
): EditableCategorizationNode[] => {
  const idx = nodes.findIndex((node) => node.key === key);
  if (idx >= 0) {
    const target = idx + offset;
    if (target < 0 || target >= nodes.length) {
      return nodes;
    }
    const reordered = [...nodes];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(target, 0, moved);
    return reordered;
  }
  return nodes.map((node) =>
    node.children.length > 0
      ? { ...node, children: moveNode(node.children, key, offset) }
      : node,
  );
};

export const countDescendants = (node: EditableCategorizationNode): number =>
  node.children.reduce(
    (total, child) => total + 1 + countDescendants(child),
    0,
  );

export const collectSecurityGroups = (
  nodes: EditableCategorizationNode[],
  groups: Set<string> = new Set<string>(),
): Set<string> => {
  nodes.forEach((node) => {
    node.createRequirements.securityGroups.forEach((grp) => groups.add(grp));

    collectSecurityGroups(node.children, groups);
  });
  return groups;
};

export const validateNodes = (
  nodes: EditableCategorizationNode[],
  level: CategorizationLevel = 1,
  path: string = '',
): CategorizationValidationError[] => {
  const errors: CategorizationValidationError[] = [];
  const seen = new Set<string>();
  const label = levelLabel(level);

  if (level === 1 && nodes.length === 0) {
    errors.push({ key: '', message: 'At least one ticket type is required.' });
  }

  nodes.forEach((node) => {
    const name = node.name.trim();
    const nodePath = path ? `${path} / ${name}` : name;
    if (name === '') {
      errors.push({
        key: node.key,
        message: path
          ? `A ${label.toLowerCase()} under "${path}" is missing a name.`
          : `A ${label.toLowerCase()} is missing a name.`,
      });
    } else if (seen.has(name.toLowerCase())) {
      errors.push({
        key: node.key,
        message: `Duplicate ${label.toLowerCase()} "${nodePath}".`,
      });
    }
    seen.add(name.toLowerCase());

    if (level < MAX_CATEGORIZATION_LEVEL) {
      errors.push(
        ...validateNodes(
          node.children,
          (level + 1) as CategorizationLevel,
          nodePath,
        ),
      );
    }
  });

  return errors;
};

// True when the node (or any descendant) matches the search text.
export const nodeMatches = (
  node: EditableCategorizationNode,
  search: string,
): boolean =>
  node.name.toLowerCase().includes(search) ||
  node.children.some((child) => nodeMatches(child, search));
